import { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import usePermissions from '../../../hooks/usePermissions';
import { useSessionValidation } from '../../../utils/sessionValidation';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Fade, Tooltip, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BillModal from './BillModal';
import Pagination from '../../common/Pagination';
import LogoLoader from '../../common/LogoLoader';
import { printBillPDF, shareBillWhatsApp } from '../../../utils/billPdfGenerator';

const PAYMENT_LABEL = { 1: 'Cash', 2: 'Card', 3: 'UPI' };

function BillIndex() {
  useSessionValidation();

  const [data, setData] = useState([]);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated, companyId, logout, token } = useAuth();
  const { canCreate, canEdit, canDelete } = usePermissions('Transaction');

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedBill, setSelectedBill] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [companyData, setCompanyData] = useState({});
  const [lookups, setLookups] = useState({});

  const today = new Date().toISOString().split('T')[0];
  const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0];
  const [fromDate, setFromDate] = useState(firstOfMonth);
  const [toDate, setToDate] = useState(today);

  useEffect(() => {
    setMounted(true);
    if (isAuthenticated && tokenIsExpired(token)) logout();
  }, [isAuthenticated, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (loading || !companyId || !token) return;
    setLoading(true);
    fetchTableData().finally(() => setLoading(false));
    fetchData('CompanyApi', `/CompanyDetails/${companyId}`, token)
      .then(r => { if (r?.responseCode === '1111' && r.response_data) setCompanyData(r.response_data); })
      .catch(() => {});
    const loadLookup = (ctrl) =>
      fetchData(ctrl, `/${companyId}`, token)
        .then(r => (r?.responseCode === '1111' && r.response_data) ? (Array.isArray(r.response_data) ? r.response_data : [r.response_data]) : [])
        .catch(() => []);
    Promise.all([
      loadLookup('FrameApi'),
      loadLookup('GlassApi'),
      loadLookup('MountApi'),
      loadLookup('BoardApi'),
      loadLookup('LaminationApi'),
      loadLookup('TapeApi'),
      loadLookup('StandApi'),
      loadLookup('PrintApi'),
    ]).then(([frames, glasses, mounts, boards, laminations, tapes, stands, prints]) => {
      setLookups({ frames, glasses, mounts, boards, laminations, tapes, stands, prints });
    });
  }, [companyId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTableData = async () => {
    try {
      const response = await fetchData('BillApi', `/BillList/${companyId}/${fromDate}/${toDate}`, token);
      if (response && response.responseCode === '1111' && response.response_data) {
        const rows = Array.isArray(response.response_data) ? response.response_data : [response.response_data];
        // Flatten Master navigation property into top-level fields for easy display
        const mapped = rows.map(item => ({
          ...item,
          ClientName:    item.ClientName    || item.Master?.Name          || '',
          ContactNumber: item.ContactNumber || item.Master?.ContactNumber || '',
        }));
        setData(mapped);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error('Error fetching Bill data:', error);
      setData([]);
    }
  };

  const handleDelete = () => {
    deleteData('BillApi', `${deleteItemId}`, token)
      .then(() => { fetchTableData(); setDeleteItemId(null); setConfirmDialogOpen(false); })
      .catch((error) => console.error('Error deleting record:', error));
  };

  const handleActivate = (itemId) => {
    activateData('BillApi', `${itemId}`, token)
      .then(() => fetchTableData())
      .catch((error) => console.error('Error activating record:', error));
  };

  const openConfirmDialog = (itemId) => { setDeleteItemId(itemId); setConfirmDialogOpen(true); };
  const closeConfirmDialog = () => { setDeleteItemId(null); setConfirmDialogOpen(false); };

  const handleOpenModal = (mode, bill = null) => { setModalMode(mode); setSelectedBill(bill); setModalOpen(true); };
  const handleCloseModal = () => { setModalOpen(false); setSelectedBill(null); fetchTableData(); };

  const enrichDetails = (rawDetails) => (rawDetails || []).map(d => ({
    ...d,
    Frame:      { Name: (lookups.frames      || []).find(x => x.Id === d.FrameId)?.Name || '' },
    Glass:      { Name: (lookups.glasses     || []).find(x => x.Id === d.GlassId)?.Name || '' },
    Lamination: { Name: (lookups.laminations || []).find(x => x.Id === d.LaminationId)?.Name || '' },
    Mount:      { Name: (lookups.mounts      || []).find(x => x.Id === d.MountId)?.Name || '' },
    Board:      { Name: (lookups.boards      || []).find(x => x.Id === d.BoardId)?.Name || '' },
    Stand:      { Name: (lookups.stands      || []).find(x => x.Id === d.StandId)?.Name || '' },
    Tape:       { Name: (lookups.tapes       || []).find(x => x.Id === d.TapeId)?.Name || '' },
    Print:      { PrintType: (lookups.prints || []).find(x => x.Id === d.PrintId)?.PrintType || (lookups.prints || []).find(x => x.Id === d.PrintId)?.Name || '' },
  }));

  const handlePrintPDF = async (item) => {
    try {
      setLoading(true);
      const response = await fetchData('BillApi', `/${item.Id}`, token);
      if (response?.responseCode === '1111' && response.response_data) {
        const fullData = {
          ...response.response_data.Bill,
          BillDetails: enrichDetails(response.response_data.BillDetails),
        };
        printBillPDF(fullData, companyData);
      } else {
        printBillPDF({ ...item, BillDetails: [] }, companyData);
      }
    } catch (err) {
      printBillPDF({ ...item, BillDetails: [] }, companyData);
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsApp = async (item) => {
    try {
      setLoading(true);
      const response = await fetchData('BillApi', `/${item.Id}`, token);
      if (response?.responseCode === '1111' && response.response_data) {
        const fullData = {
          ...response.response_data.Bill,
          BillDetails: enrichDetails(response.response_data.BillDetails),
        };
        await shareBillWhatsApp(fullData, companyData);
      } else {
        await shareBillWhatsApp({ ...item, BillDetails: [] }, companyData);
      }
    } catch (err) {
      await shareBillWhatsApp({ ...item, BillDetails: [] }, companyData);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = data.filter(item =>
    (item.ClientName || item.Master?.Name || '')?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.ContactNumber || item.Master?.ContactNumber || '')?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(item.BillNumber)?.includes(searchTerm)
  );
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const renderActions = (item) => (
    <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
      <Tooltip title="View Details" arrow>
        <Button size="small" onClick={() => handleOpenModal('view', item)}
          style={{ minWidth: '22px', height: '22px', borderRadius: '6px', color: 'white', padding: '2px', border: 'none', fontSize: '10px', fontWeight: '600', boxShadow: '0 1px 4px rgba(102,126,234,0.3)' }}>
          👁️
        </Button>
      </Tooltip>
      <Tooltip title="Print Bill" arrow>
        <Button size="small" onClick={() => handlePrintPDF(item)}
          style={{ minWidth: '22px', height: '22px', borderRadius: '6px', color: 'white', padding: '2px', border: 'none', fontSize: '10px', fontWeight: '600', background: '#e65100', boxShadow: '0 1px 4px rgba(230,81,0,0.3)' }}>
          🖨️
        </Button>
      </Tooltip>
      <Tooltip title="Share on WhatsApp" arrow>
        <Button size="small" onClick={() => handleWhatsApp(item)}
          style={{ minWidth: '22px', height: '22px', borderRadius: '6px', color: 'white', padding: '2px', border: 'none', fontSize: '10px', fontWeight: '600', background: '#25D366', boxShadow: '0 1px 4px rgba(37,211,102,0.4)' }}>
          💬
        </Button>
      </Tooltip>
      {canEdit && (
        <Tooltip title="Edit Bill" arrow>
          <Button size="small" onClick={() => handleOpenModal('edit', item)}
            style={{ minWidth: '22px', height: '22px', borderRadius: '6px', color: 'white', padding: '2px', border: 'none', fontSize: '10px', fontWeight: '600', boxShadow: '0 1px 4px rgba(17,153,142,0.3)' }}>
            ✏️
          </Button>
        </Tooltip>
      )}
      {canDelete && (
        <Tooltip title={item.IsActive ? 'Delete Bill' : 'Activate Bill'} arrow>
          <Button size="small" onClick={() => item.IsActive ? openConfirmDialog(item.Id) : handleActivate(item.Id)}
            style={{ minWidth: '22px', height: '22px', borderRadius: '6px', color: 'white', padding: '2px', border: 'none', fontSize: '10px', fontWeight: '600', boxShadow: item.IsActive ? '0 1px 4px rgba(255,107,107,0.3)' : '0 1px 4px rgba(78,205,196,0.3)' }}>
            {item.IsActive ? '🗑️' : '✅'}
          </Button>
        </Tooltip>
      )}
    </div>
  );

  const fmt = (v) => parseFloat(v || 0).toFixed(2);
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN') : '—';

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{ minWidth: '100%', background: '#fdf6ec', padding: '10px', position: 'relative' }}>
        {loading && <LogoLoader open={loading} message="Loading Bills..." />}

        <div style={{ maxWidth: '1400px', margin: '0 auto', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '12px', color: 'black', fontFamily: 'Times New Romans' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
            <h4 style={{ fontSize: '17px', fontWeight: '700', margin: 0, letterSpacing: '-0.3px', fontFamily: 'Times New Romans', textTransform: 'uppercase' }}>
              <span style={{ color: 'black' }}>🧾 Bills</span>
            </h4>
          </div>

          {/* Filters + Search + Add */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
            <TextField
              label="From Date" type="date" size="small" value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: '150px', '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#fdf6ec', fontSize: '13px', height: '29px', '&:hover fieldset': { borderColor: '#c9a84c' }, '&.Mui-focused fieldset': { borderColor: '#c9a84c' } }, '& .MuiInputLabel-root': { fontSize: '12px', '&.Mui-focused': { color: '#c9a84c' } } }}
            />
            <TextField
              label="To Date" type="date" size="small" value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: '150px', '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#fdf6ec', fontSize: '13px', height: '29px', '&:hover fieldset': { borderColor: '#c9a84c' }, '&.Mui-focused fieldset': { borderColor: '#c9a84c' } }, '& .MuiInputLabel-root': { fontSize: '12px', '&.Mui-focused': { color: '#c9a84c' } } }}
            />
            <Tooltip title="Fetch bills for selected date range" arrow>
              <Button variant="contained" onClick={() => { setCurrentPage(1); setLoading(true); fetchTableData().finally(() => setLoading(false)); }}
                style={{ background: 'linear-gradient(135deg, #5c3d1e 0%, #5c3d1e 100%)', color: 'white', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', border: 'none', minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(92,61,30,0.4)' }}>
                🔍 Search
              </Button>
            </Tooltip>
            <TextField
              size="small" placeholder="Search by client, contact or bill no..." value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon style={{ color: '#c9a84c', fontSize: '20px' }} /></InputAdornment> }}
              sx={{ flex: 1, minWidth: '180px', '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#fdf6ec', fontSize: '13px', height: '29px', '&:hover fieldset': { borderColor: '#c9a84c' }, '&.Mui-focused fieldset': { borderColor: '#c9a84c' } } }}
            />
            {canCreate && (
              <Tooltip title="Add New Bill" arrow>
                <Button variant="contained" onClick={() => handleOpenModal('create')}
                  style={{ background: 'linear-gradient(135deg, #5c3d1e 0%, #5c3d1e 100%)', color: 'white', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', border: 'none', minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(92,61,30,0.4)' }}>
                  ➕ Add Bill
                </Button>
              </Tooltip>
            )}
          </div>

          {/* Table */}
          <div style={{ marginBottom: '10px' }}>
            <TableContainer component={Paper} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderRadius: '10px', overflow: 'hidden' }}>
              <Table sx={{ minWidth: 700 }} aria-label="Bill table">
                <TableHead>
                  <TableRow style={{ background: 'linear-gradient(135deg, #5c3d1e 0%, #5c3d1e 100%)', height: '27px' }}>
                    {['Bill #', 'Date', 'Client', 'Contact', 'Payment', 'Total (₹)', 'Balance (₹)', 'Actions'].map(h => (
                      <TableCell key={h} style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 10px', textAlign: h.includes('₹') ? 'right' : h === 'Actions' ? 'right' : 'left' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} style={{ textAlign: 'center', padding: '24px', color: '#999', fontSize: '13px' }}>
                        No bills found
                      </TableCell>
                    </TableRow>
                  ) : paginatedData.map((item, index) => (
                    <TableRow key={item.Id} style={{ background: index % 2 === 0 ? '#ffffff' : '#fdf6ec', borderBottom: '2px solid #f0f0f0' }}>
                      <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#2d3748', fontWeight: '700' }}>{companyData.BillPrefix || ''}{item.BillNumber}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#2d3748' }}>{fmtDate(item.BillDate)}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#2d3748', fontWeight: '600' }}>{item.ClientName || '—'}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#2d3748' }}>{item.ContactNumber || '—'}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#2d3748' }}>{PAYMENT_LABEL[item.PaymentType] || '—'}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#2d3748', textAlign: 'right', fontWeight: '600' }}>₹{fmt(item.TotalAmount)}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '6px 10px', textAlign: 'right', fontWeight: '600', color: item.BalanceAmount > 0 ? '#d32f2f' : '#388e3c' }}>₹{fmt(item.BalanceAmount)}</TableCell>
                      <TableCell style={{ padding: '6px 10px', textAlign: 'right' }}>{renderActions(item)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          <Pagination
            currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage} onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </div>

        {/* Delete Confirm */}
        <Dialog open={confirmDialogOpen} onClose={closeConfirmDialog} PaperProps={{ style: { borderRadius: '12px', padding: '8px' } }}>
          <DialogTitle style={{ fontSize: '16px', fontWeight: '600', color: '#2d3748' }}>Delete Record?</DialogTitle>
          <DialogContent>
            <DialogContentText style={{ fontSize: '13px', color: '#666' }}>Are you sure you want to delete this Bill record?</DialogContentText>
          </DialogContent>
          <DialogActions style={{ padding: '8px 12px', gap: '6px' }}>
            <Button onClick={closeConfirmDialog} style={{ background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)', color: '#ffffff', borderRadius: '6px', padding: '4px 8px', fontSize: '12px', fontWeight: '600', textTransform: 'none', border: 'none', minHeight: '26px' }}>
              ❌ Cancel
            </Button>
            <Button onClick={handleDelete} style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: '#ffffff', borderRadius: '6px', padding: '4px 8px', fontSize: '12px', fontWeight: '600', textTransform: 'none', border: 'none', minHeight: '26px' }}>
              ✅ Confirm Delete
            </Button>
          </DialogActions>
        </Dialog>

        <BillModal open={modalOpen} onClose={handleCloseModal} onSave={fetchTableData} BillData={selectedBill} mode={modalMode} companyData={companyData} />
      </div>
    </Fade>
  );
}

export default BillIndex;
